import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { Adapter } from "src/app/core/adapter";
import { Model } from "src/app/core/models/model";
import { GraphElementDto } from "src/app/shared/components/graph/dto/graph-element-dto";
import { ILineElement } from "src/app/shared/components/line-chart/interfaces/iline-element";
import { UtilsService } from "src/app/shared/services/utils.service";
import { GraphGrouping, Timeframe } from "src/app/shared/types/hydrao-types";
import { Shower } from "./shower.model";
import { User } from "./user.model";

export class GraphElement implements Model, GraphElementDto {
  constructor(public date: DateTime, public volume: number) {}

  public get xLabel(): String {
    return this.date.toISO();
  }
  public get barValue(): number {
    return this.volume;
  }
  public bar2Value: number = null;
  public lineValue: number = null;
  public line2Value: number = null;
  public barStyle: string = null;
}

export class BarGraphElement implements Model, GraphElementDto {
  constructor(
    public date: DateTime,
    public volume_meter: number,
    public volume_sh: number
  ) {}

  public get xLabel(): String {
    return this.date.toISO();
  }
  public get barValue(): number {
    return this.volume_sh;
  }
  public get bar2Value(): number {
    return this.volume_meter;
  }
  public lineValue: number = null;
  public line2Value: number = null;
  public barStyle: string = null;
}

export class ShGraphElement implements Model {
  constructor(
    public date: DateTime,
    public nb_showers: number,
    public total_vol: number,
    public total_duration: number
  ) {}
  // public get xLabel(): String {
  //     return this.date.toISOString();
  // }
  // public get barValue(): number {
  //     return this.total_vol;
  // };
  // public lineValue: number = null;
}

export class MeterGraphElement implements Model {
  constructor(
    public date: DateTime,
    public average_flow: number,
    public total_duration: number,
    public total_vol: number
  ) {}
  // public get xLabel(): String {
  //     return this.date.toISOString();
  // }
  // public get barValue(): number {
  //     return this.total_vol;
  // };
  // public lineValue: number = null;
}

export class ShStatsDetails implements Model {
  constructor(public grouping: string, public data: ShGraphElement[]) {}

  public get isDayGrouping() {
    return this.grouping == "day";
  }

  public get isMonthGrouping() {
    return this.grouping == "month";
  }

  public get isHourGrouping() {
    return this.grouping == "hour";
  }
}

export enum MetricCategory {
  showerhead,
  meter,
}

export enum MetricType {
  volume,
  temperature,
  duration,
  other,
}

export enum ShowerHeadMetricId {
  volume = "volume",
  nb_showers = "nb_showers",
  avg_shower = "avg_shower",
  avg_duration = "avg_duration",
  nb_sh = "nb_sh",
  total_shower_per_day = "total_shower_per_day",
}

export enum MeterMetricId {
  meter_volume = "meter_volume",
}

export class MetricElement {
  public type: MetricType;
  public category: MetricCategory;
  public label: String;
  public svgIcon: String = null;
  public matIcon: String = null;
  public id: ShowerHeadMetricId | MeterMetricId;
  public showed: boolean = false;

  constructor(public value: number, id: ShowerHeadMetricId | MeterMetricId) {
    this.svgIcon = null;
    this.matIcon = null;
    this.id = id;
    switch (id) {
      case ShowerHeadMetricId.volume:
        this.type = MetricType.volume;
        this.category = MetricCategory.showerhead;
        this.label = $localize`total shower volume`;
        this.svgIcon = "drop";
        break;
      case ShowerHeadMetricId.nb_showers:
        this.type = MetricType.other;
        this.category = MetricCategory.showerhead;
        this.label = $localize`total number of showers`;
        this.svgIcon = "shower";
        break;
      case ShowerHeadMetricId.avg_shower:
        this.type = MetricType.volume;
        this.category = MetricCategory.showerhead;
        this.label = $localize`average shower volume`;
        this.svgIcon = "water_usage";
        break;
      case ShowerHeadMetricId.avg_duration:
        this.type = MetricType.duration;
        this.category = MetricCategory.showerhead;
        this.label = $localize`average shower duration`;
        this.svgIcon = "chrono";
        break;
      case ShowerHeadMetricId.total_shower_per_day:
        this.type = MetricType.other;
        this.category = MetricCategory.showerhead;
        this.label = $localize`total shower per day`;
        this.matIcon = "today";
        break;
      // case ShowerHeadMetricId.nb_places:
      //     this.type = MetricType.other;
      //     this.category = MetricCategory.place;
      //     this.label = $localize`number of places`;
      //     this.matIcon = 'location_city';
      //     break;
      case ShowerHeadMetricId.nb_sh:
        this.type = MetricType.other;
        this.category = MetricCategory.showerhead;
        this.label = $localize`number of showerheads`;
        this.svgIcon = "shower_head";
        break;
      case MeterMetricId.meter_volume:
        this.type = MetricType.volume;
        this.category = MetricCategory.meter;
        this.label = $localize`total meter volume`;
        this.svgIcon = "meter";
        break;
    }
  }

  toMetricBloc(): MetricBloc {
    return {
      id: this.id.toString(),
      label: this.label,
      type: this.type.toString(),
      svgIcon: this.svgIcon,
      matIcon: this.matIcon,
      value: this.value,
    } as MetricBloc;
  }
}

export interface ShowerheadsGraphStats {
  volume: number;
  nb_showers: number;
  avg_shower: number;
  avg_duration: number;
  total_shower_per_day: number;
  withLive: boolean;
}

export function getShMetricsFromGraphStats(
  graphStats: ShowerheadsGraphStats,
  utils: UtilsService,
  user: User,
  timeframe: Timeframe,
  userMetrics: string[]
): MetricBloc[] {
  const metrics: MetricBloc[] = [];

  if (!graphStats) return metrics;

  let value;

  if (graphStats.volume > 0) {
    value = utils.getVolumeFormattedWithUser(user, graphStats.volume);
    const metric: any = {
      id: "volume",
      label: $localize`total shower volume`,
      type: "volume",
      unit: value.unit,
      value: value.value,
      viewed: userMetrics.indexOf("volume") >= 0,
      svgIcon: "drop",
    };
    if (graphStats.withLive === true) {
      metric.help = $localize`The data includes an ongoing shower and may therefore change.`;
    }
    metrics.push(metric);
  }

  if (graphStats.nb_showers > 0) {
    metrics.push({
      id: "nb_showers",
      label: $localize`total number of showers`,
      type: "other",
      unit: null,
      value: graphStats.nb_showers,
      viewed: userMetrics.indexOf("nb_showers") >= 0,
      svgIcon: "shower",
    });
  }

  if (graphStats.avg_shower > 0) {
    value = utils.getVolumeFormattedWithUser(user, graphStats.avg_shower);
    metrics.push({
      id: "avg_shower",
      label: $localize`average shower volume`,
      type: "volume",
      unit: value.unit,
      value: value.value,
      viewed: userMetrics.indexOf("avg_shower") >= 0,
      svgIcon: "water_usage",
    });
  }

  if (graphStats.avg_duration > 0) {
    metrics.push({
      id: "avg_duration",
      label: $localize`average shower duration`,
      type: "duration",
      unit: null,
      value: graphStats.avg_duration,
      viewed: userMetrics.indexOf("avg_duration") >= 0,
      svgIcon: "chrono",
    });
  }

  if (graphStats.total_shower_per_day > 0 && timeframe != Timeframe.Day) {
    metrics.push({
      id: "total_shower_per_day",
      label: $localize`total shower per day`,
      type: "other",
      unit: null,
      value: graphStats.total_shower_per_day,
      viewed: userMetrics.indexOf("total_shower_per_day") >= 0,
      svgIcon: "today",
    });
  }

  return metrics;
}

export function getShMetricsFromShower(
  shower: Shower,
  utils: UtilsService,
  user: User
): MetricBloc[] {
  const metrics: MetricBloc[] = [];

  if (!shower) return metrics;

  let value;

  if (shower.volume > 0) {
    value = utils.getVolumeFormattedWithUser(user, shower.volume);
    const metric: any = {
      id: "volume",
      label: $localize`volume`,
      type: "volume",
      unit: value.unit,
      value: value.value,
      viewed: true,
    };

    metrics.push(metric);
  }

  if (shower.is_live === true) {
    //TODO
  }

  if (shower.flow > 0) {
    value = utils.getFlowFormattedWithUser(user, shower.flow);
    metrics.push({
      id: "flow",
      label: $localize`flow`,
      type: "flow",
      unit: value.unit,
      value: value.value,
      viewed: true,
    });
  }

  if (shower.temperature > 0) {
    value = utils.getTemperatureFormattedWithUser(user, shower.temperature);
    metrics.push({
      id: "temperature",
      label: $localize`temperature`,
      type: "temperature",
      unit: value.unit,
      value: value.value,
      viewed: true,
    });
  }

  if (shower.duration > 0) {
    metrics.push({
      id: "duration",
      label: $localize`duration`,
      type: "duration",
      unit: null,
      value: shower.duration,
      viewed: true,
    });
  }

  if (shower.soaping_time > 0) {
    metrics.push({
      id: "soaping_time",
      label: $localize`soaping time`,
      type: "duration",
      unit: null,
      value: shower.soaping_time,
      viewed: true,
    });
  }

  if (shower.number_of_soapings > 0) {
    metrics.push({
      id: "number_of_soapings",
      label: $localize`soapings count`,
      type: "other",
      unit: null,
      value: shower.number_of_soapings,
      viewed: true,
    });
  }

  return metrics;
}

export interface MetersGraphStats {
  meter_volume: number;
}

export function getMeterMetricsFromGraphStats(
  graphStats: MetersGraphStats,
  utils: UtilsService,
  user: User
): MetricBloc[] {
  const metrics: MetricBloc[] = [];

  if (!graphStats) return metrics;

  let userMetrics = [];
  if (user && user.dashboard && user.dashboard.meter) {
    userMetrics = user.dashboard.meter;
  }

  if (graphStats.meter_volume > 0) {
    let value = utils.getVolumeFormattedWithUser(user, graphStats.meter_volume);
    metrics.push({
      id: "meter_volume",
      label: $localize`total meter volume`,
      type: "volume",
      unit: value.unit,
      value: value.value,
      viewed: userMetrics.indexOf("meter_volume") >= 0,
      svgIcon: "meter",
    });
  }

  return metrics;
}

export interface MetricBloc {
  id: string;
  label: string;
  unit: string;
  type?: string;
  matIcon?: string;
  svgIcon?: string;
  value: number;
  help?: string;
  viewed: boolean;
}

export class GlobalStats implements Model {
  public metrics: MetricElement[];

  private metricMap: Map<ShowerHeadMetricId, MetricElement>;

  public details: GraphElement[];

  public lineChart: ILineElement[];

  public barChart: BarGraphElement[];

  public buildMetricList(
    metricIds: string[],
    canViewMeter = true,
    canViewSh = true,
    canViewPlace = true
  ) {
    this.metrics = new Array<MetricElement>();

    for (let metricId of metricIds) {
      let metric: MetricElement = this.metricMap[metricId];
      if (
        metric &&
        (canViewMeter || metric.category !== MetricCategory.meter) &&
        (canViewSh || metric.category !== MetricCategory.showerhead)
      ) {
        this.metrics.push(metric);
      }
    }
  }

  public buildGraph(from: DateTime, to: DateTime, grouping: GraphGrouping) {
    //we assume that graph elements are already ordered
    if (!this.sh_details && !this.meter_details) {
      return; //nothing to do
    }

    if (!from) {
      if (this.sh_details && this.sh_details.length > 0) {
        from = this.sh_details[0].date;
      }
      if (this.meter_details && this.meter_details.length > 0) {
        if ((from && this.meter_details[0].date < from) || !from) {
          from = this.meter_details[0].date;
        }
      }
    }

    if (!to) {
      if (this.sh_details && this.sh_details.length > 0) {
        to = this.sh_details[this.sh_details.length - 1].date;
      }
      if (this.meter_details && this.meter_details.length > 0) {
        if (
          (to && this.meter_details[this.meter_details.length - 1].date > to) ||
          !to
        ) {
          to = this.meter_details[this.meter_details.length - 1].date;
        }
      }
    }
    let addQty = null;
    let compareQty = null;

    switch (grouping) {
      case GraphGrouping.months:
        addQty = "months";
        compareQty = "month";
        break;
      case GraphGrouping.days:
        addQty = "days";
        compareQty = "day";
        break;
      case GraphGrouping.hours:
        addQty = "hours";
        compareQty = "hour";
        break;
    }
    if (addQty && compareQty) {
      this.details = [];
      for (
        let curElem: DateTime = from;
        curElem <= to;
        curElem = curElem.plus({ [addQty]: 1 })
      ) {
        let volShower = null,
          volMeter = null;
        if (this.sh_details) {
          let sElem = this.sh_details.find((s) =>
            s.date.hasSame(curElem, compareQty)
          );
          if (sElem) volShower = sElem.total_vol;
        }
        if (this.meter_details) {
          let mElem = this.meter_details.find((s) =>
            s.date.hasSame(curElem, compareQty)
          );
          if (mElem) volMeter = mElem.total_vol;
        }
        if (this.sh_details) {
          this.details.push(new GraphElement(curElem, volShower));
        } else if (this.meter_details) {
          this.details.push(new GraphElement(curElem, volMeter));
        }
      }
    }
  }

  constructor(
    public volume: number,
    public meter_volume: number,
    public nb_showers: number,
    public nb_places: number,
    public nb_sh: number,
    public nb_sh_active_today: number,
    public nb_meter: number,
    public nb_meter_active_today: number,
    public avg_shower: number,
    public avg_duration: number,
    public total_shower_per_day: number,
    public sh_details: ShGraphElement[],
    public meter_details: MeterGraphElement[],
    public last_meter_update: Date,
    public last_sh_update: Date
  ) {
    this.metricMap = new Map<ShowerHeadMetricId, MetricElement>();
    if (volume !== null) {
      this.metricMap[ShowerHeadMetricId.volume] = new MetricElement(
        volume,
        ShowerHeadMetricId.volume
      );
    }

    if (nb_showers !== null) {
      this.metricMap[ShowerHeadMetricId.nb_showers] = new MetricElement(
        nb_showers,
        ShowerHeadMetricId.nb_showers
      );
    }
    // if (nb_places !== null) {
    //     this.metricMap[ShowerHeadMetricId.nb_places] = new MetricElement(nb_places, ShowerHeadMetricId.nb_places);
    // }
    if (nb_sh !== null) {
      this.metricMap[ShowerHeadMetricId.nb_sh] = new MetricElement(
        nb_sh,
        ShowerHeadMetricId.nb_sh
      );
    }
    if (avg_shower !== null) {
      this.metricMap[ShowerHeadMetricId.avg_shower] = new MetricElement(
        avg_shower,
        ShowerHeadMetricId.avg_shower
      );
    }
    if (avg_duration !== null) {
      this.metricMap[ShowerHeadMetricId.avg_duration] = new MetricElement(
        avg_duration,
        ShowerHeadMetricId.avg_duration
      );
    }
    if (total_shower_per_day !== null) {
      this.metricMap[ShowerHeadMetricId.total_shower_per_day] =
        new MetricElement(
          total_shower_per_day,
          ShowerHeadMetricId.total_shower_per_day
        );
    }
    if (meter_volume !== null) {
      this.metricMap[MeterMetricId.meter_volume] = new MetricElement(
        meter_volume,
        MeterMetricId.meter_volume
      );
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class ShGraphElementAdapter implements Adapter<ShGraphElement> {
  constructor() {}
  adapt(item: any): ShGraphElement {
    return new ShGraphElement(
      DateTime.fromISO(item.date, { zone: Settings.defaultZone }),
      item.nb_showers,
      item.total_vol ? item.total_vol : item.volume,
      item.total_duration ? item.total_duration : item.duration
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class MeterGraphElementAdapter implements Adapter<MeterGraphElement> {
  constructor() {}
  adapt(item: any): MeterGraphElement {
    return new MeterGraphElement(
      DateTime.fromISO(item.date, { zone: Settings.defaultZone }),
      item.average_flow,
      item.total_duration ? item.total_duration : item.duration,
      item.total_vol ? item.total_vol : item.volume
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class GlobalStatsAdapter implements Adapter<GlobalStats> {
  constructor(
    private shGraphElementAdapter: ShGraphElementAdapter,
    private meterGraphElementAdapter: MeterGraphElementAdapter
  ) {}
  adapt(item: any): GlobalStats {
    // * 1 is to convert null values to 0
    return new GlobalStats(
      item.hasOwnProperty("volume") ? item.volume * 1 : null,
      item.hasOwnProperty("meter_volume") ? item.meter_volume * 1 : null,
      item.hasOwnProperty("nb_showers") ? item.nb_showers * 1 : null,
      item.hasOwnProperty("nb_places") ? item.nb_places * 1 : null,
      item.hasOwnProperty("nb_sh") ? item.nb_sh * 1 : null,
      item.hasOwnProperty("nb_sh_active_today")
        ? item.nb_sh_active_today * 1
        : null,
      item.hasOwnProperty("nb_meter") ? item.nb_meter * 1 : null,
      item.hasOwnProperty("nb_meter_active_today")
        ? item.nb_meter_active_today * 1
        : null,
      item.hasOwnProperty("avg_shower") ? item.avg_shower * 1 : null,
      item.hasOwnProperty("avg_duration") ? item.avg_duration * 1 : null,
      item.hasOwnProperty("total_shower_per_day")
        ? item.total_shower_per_day * 1
        : null,
      item.hasOwnProperty("sh_details") && Array.isArray(item.sh_details)
        ? item.sh_details.map((i) => this.shGraphElementAdapter.adapt(i))
        : null,
      item.hasOwnProperty("meter_details") && Array.isArray(item.meter_details)
        ? item.meter_details.map((i) => this.meterGraphElementAdapter.adapt(i))
        : null,
      item.last_meter_update ? new Date(item.last_meter_update) : null,
      item.last_sh_update ? new Date(item.last_sh_update) : null
    );
  }
}
